import { ChangeDetectorRef, Component, ViewChild } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { MachineType } from '../../../../core/enums/machine-type';
import { ProductionTechnicianArea, ProductionTechnicianAreaLabels } from '../../../../core/enums/production-technician-area';
import { ColumnManagerService } from '../../../../core/services/column-manager/column-manager.service';
import { MachinePerformanceListService } from '../../../../core/services/machine-performance-list/machine-performance-list.service';
import { GenericCellType } from '../../../../core/models/column-definition';
import { DefaultMachinePerformanceListDto } from '../../../../core/models/machine-performances/default-machine-performance-list-dto';
import { MachinePerformanceType, MachinePerformanceTypeLabels } from '../../../../core/models/machine-performances/machine-performance-type';
import { MachineSize, MachineSizeLabels } from '../../../../core/models/machines/machine-size';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FilterButtonComponent } from '../../../../core/components/filter-button/filter-button.component';
import { FilterInputChipsComponent } from '../../../../core/components/filter-input-chips/filter-input-chips.component';
import { DefaultMachinePerformanceListFilter } from '../../../../core/models/machine-performances/default-machine-performance-list-filter';
import { FilterItem } from '../../../../core/models/filter-item';

@Component({
  selector: 'app-machine-performance-list',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatPaginatorModule,
    MatSelectModule,
    MatSortModule,
    MatTableModule,
    MatTabsModule,
    RouterModule,
    MatTooltipModule,
    FilterButtonComponent,
    FilterInputChipsComponent
  ],
  templateUrl: './machine-performance-list.component.html',
  styleUrl: './machine-performance-list.component.scss'
})
export class MachinePerformanceListComponent {
  private readonly ngUnsubscribe$ = new Subject<void>();
  readonly tableId = 'machine-performance-table';

  readonly machinePerformanceTypes = Object.values(MachinePerformanceType).filter((value): value is MachinePerformanceType => typeof value === 'number');
  protected readonly MachineType = MachineType;
  readonly machinePerformanceTypeLabels = MachinePerformanceTypeLabels;
  readonly areas = Object.values(ProductionTechnicianArea).filter((value): value is ProductionTechnicianArea => typeof value === 'number');
  readonly productionTechnicianAreaLabels = ProductionTechnicianAreaLabels;
  readonly sizes = Object.values(MachineSize).filter((value): value is MachineSize => typeof value === 'number');
  readonly machineSizeLabels = MachineSizeLabels;

  machinePerformancesDataSource = new MatTableDataSource<DefaultMachinePerformanceListDto>([]);
  machinePerformances: DefaultMachinePerformanceListDto[] = [];
  machineType: MachineType = MachineType.Forwarder;
  displayedColumns: string[] = [];
  isLoading = true;
  readonly skeletonRowCount = 20;

  modifiedRows = new Set<number>();
  newItem = this.createNewItem();
  isAddingRow = false;
  private pendingAddition = false;
  private previousMaxId = 0;
  private highlightedRowIds = new Set<number>();
  private highlightClearTimeoutId: ReturnType<typeof setTimeout> | null = null;
  selectedTabIndex = 0;
  allMachinePerformanceTypes: string[] = [];
  allProductionTechnicianAreas: string[] = [];
  allMachineSizes: string[] = [];
  filterValues: DefaultMachinePerformanceListFilter = {
    search: [],
    type: [],
    area: [],
    size: []
  };

  @ViewChild(MatPaginator) paginator?: MatPaginator;
  @ViewChild(MatSort) sort?: MatSort;

  constructor(
    private readonly machinePerformanceListService: MachinePerformanceListService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly columnManagerService: ColumnManagerService
  ) { }

  get pageSizeOptions(): number[] {
    const baseOptions = [20, 30, 50];
    const total = this.machinePerformances.length;

    if (total > 0 && !baseOptions.includes(total)) {
      return [...baseOptions, total];
    }

    return baseOptions;
  }

  ngOnInit(): void {
    this.machinePerformanceListService.filterChange
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(filter => this.applyFilter(filter));

    this.observeData();
    this.loadData();
  }

  ngAfterViewInit(): void {
    if (!this.paginator) {
      return;
    }

    this.machinePerformancesDataSource.paginator = this.paginator;
    if (this.sort) {
      this.machinePerformancesDataSource.sort = this.sort;
      this.machinePerformancesDataSource.sortingDataAccessor = (item, property) => {
        switch (property) {
          case 'type':
            return this.getMachinePerformanceTypeLabel(item.type).toLocaleLowerCase('sv');
          case 'area':
            return this.getAreaLabel(item.area).toLocaleLowerCase('sv');
          case 'size':
            return this.getMachineSizeLabel(item.size).toLocaleLowerCase('sv');
          default:
            return (item as any)[property];
        }
      };
    }

    const savedPageSize = this.columnManagerService.loadPageSize(this.tableId);
    if (savedPageSize && savedPageSize > 0) {
      this.paginator.pageSize = savedPageSize;
    }

    this.paginator.page
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(({ pageSize }) => this.columnManagerService.savePageSize(this.tableId, pageSize));
  }

  ngOnDestroy(): void {
    this.clearHighlightTimer();
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  addItem(): void {
    if (!this.isValidNewItem()) {
      return;
    }

    this.newItem.machineType = this.machineType;

    const newItemPayload: DefaultMachinePerformanceListDto = {
      ...this.newItem,
      machineType: this.machineType
    };

    this.pendingAddition = true;
    this.previousMaxId = this.getCurrentMaxId();
    this.isAddingRow = true;

    this.machinePerformanceListService.addMachinePerformance(newItemPayload, this.machineType)
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe({
        next: () => {
          this.newItem = this.createNewItem();
        },
        error: () => {
          this.isAddingRow = false;
          this.pendingAddition = false;
        }
      });
  }

  deleteRow(element: DefaultMachinePerformanceListDto): void {
    this.machinePerformanceListService.deleteMachinePerformance(element);
  }

  isRowModified(rowIndex: number): boolean {
    return this.modifiedRows.has(rowIndex);
  }

  onRowChanged(rowIndex: number): void {
    this.modifiedRows.add(rowIndex);
  }

  saveRow(element: DefaultMachinePerformanceListDto, index: number): void {
    this.machinePerformanceListService.updateMachinePerformance(element);
    this.modifiedRows.delete(index);
  }

  selectedTabChange(event: MatTabChangeEvent): void {
    this.selectedTabIndex = event.index;
    this.machineType = event.index === 0
      ? MachineType.Forwarder
      : MachineType.Harvester;
    this.loadData();
  }

  private observeData(): void {
    this.machinePerformanceListService.data$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(machinePerformances => {
        if (!machinePerformances) {
          this.isLoading = true;
          this.machinePerformances = [];
          this.machinePerformancesDataSource.data = this.createSkeletonRows();
          this.modifiedRows.clear();
          this.isAddingRow = false;
          this.pendingAddition = false;
          this.previousMaxId = 0;
          return;
        }

        this.isLoading = false;
        this.machinePerformances = machinePerformances;
        this.initializeFilters();
        this.applyFilter(this.filterValues);

        if (this.pendingAddition) {
          const newRows = machinePerformances.filter(performance => performance.id > this.previousMaxId);

          if (newRows.length > 0) {
            this.highlightedRowIds = new Set(newRows.map(row => row.id));
            this.scheduleHighlightClear();
          } else {
            this.highlightedRowIds.clear();
          }

          this.pendingAddition = false;
        }

        this.previousMaxId = this.getCurrentMaxId();
        this.isAddingRow = false;

        if (this.paginator) {
          this.machinePerformancesDataSource.paginator = this.paginator;

          const maxPageIndex = Math.max(0, Math.ceil(this.machinePerformancesDataSource.data.length / this.paginator.pageSize) - 1);
          if (this.paginator.pageIndex > maxPageIndex) {
            this.paginator.firstPage();
          }
        }
      });
  }

  private loadData(): void {
    this.isLoading = true;
    this.modifiedRows.clear();
    this.highlightedRowIds.clear();
    this.clearHighlightTimer();
    this.pendingAddition = false;
    this.isAddingRow = false;
    this.updateDisplayedColumns();
    this.machinePerformancesDataSource.data = this.createSkeletonRows();
    this.machinePerformanceListService.loadMachinePerformances(this.machineType);
    this.newItem = this.createNewItem();

    if (this.paginator) {
      this.paginator.firstPage();
    }
  }

  private updateDisplayedColumns(): void {
    this.displayedColumns = this.machineType === MachineType.Forwarder
      ? ['type', 'area', 'size', 'g5InM3FubPerHour', 'transportDistanceForwarding', 'action']
      : ['type', 'area', 'g5InM3FubPerHour', 'meanStemVolumeInM3FubPerTree', 'action'];
  }

  onFiltersChanged(): void {
    this.filterValues = { ...this.filterValues };
    this.machinePerformanceListService.applyFilter(this.filterValues);
  }

  private initializeFilters(): void {
    this.allMachinePerformanceTypes = this.getSortedUniqueLabels(this.machinePerformances, element => this.getMachinePerformanceTypeLabel(element.type));
    this.allProductionTechnicianAreas = this.getSortedUniqueLabels(this.machinePerformances, element => this.getAreaLabel(element.area));
    this.allMachineSizes = this.machineType === MachineType.Forwarder
      ? this.getSortedUniqueLabels(this.machinePerformances, element => this.getMachineSizeLabel(element.size))
      : [];

    this.filterValues = {
      ...this.filterValues,
      type: this.filterValues.type.filter(item => this.allMachinePerformanceTypes.includes(item.value)),
      area: this.filterValues.area.filter(item => this.allProductionTechnicianAreas.includes(item.value)),
      size: this.filterValues.size.filter(item => this.allMachineSizes.includes(item.value))
    };
  }

  private getSortedUniqueLabels(
    items: DefaultMachinePerformanceListDto[],
    selector: (item: DefaultMachinePerformanceListDto) => string
  ): string[] {
    return [...new Set(items.map(selector).filter(value => !!value))]
      .sort((a, b) => a.localeCompare(b, 'sv'));
  }

  private applyFilter(filterValues: DefaultMachinePerformanceListFilter): void {
    if (this.isLoading) {
      return;
    }

    this.filterValues = { ...filterValues };

    let filteredValues = [...this.machinePerformances];

    if (this.filterValues.type.length) {
      filteredValues = filteredValues.filter(item => this.isPropertyFiltered(this.filterValues.type, this.getMachinePerformanceTypeLabel(item.type)));
    }

    if (this.filterValues.area.length) {
      filteredValues = filteredValues.filter(item => this.isPropertyFiltered(this.filterValues.area, this.getAreaLabel(item.area)));
    }

    if (this.machineType === MachineType.Forwarder && this.filterValues.size.length) {
      filteredValues = filteredValues.filter(item => this.isPropertyFiltered(this.filterValues.size, this.getMachineSizeLabel(item.size)));
    }

    this.machinePerformancesDataSource.data = this.sortByDefaultOrder(filteredValues);

    if (this.paginator) {
      this.machinePerformancesDataSource.paginator = this.paginator;
      const maxPageIndex = Math.max(0, Math.ceil(filteredValues.length / this.paginator.pageSize) - 1);
      if (this.paginator.pageIndex > maxPageIndex) {
        this.paginator.firstPage();
      }
    }

    if (this.sort) {
      this.machinePerformancesDataSource.sort = this.sort;
    }
  }

  private sortByDefaultOrder(items: DefaultMachinePerformanceListDto[]): DefaultMachinePerformanceListDto[] {
    return [...items].sort((a, b) => {
      const typeSort = this.compareLocalized(this.getMachinePerformanceTypeLabel(a.type), this.getMachinePerformanceTypeLabel(b.type));
      if (typeSort !== 0) {
        return typeSort;
      }

      const areaSort = this.compareLocalized(this.getAreaLabel(a.area), this.getAreaLabel(b.area));
      if (areaSort !== 0) {
        return areaSort;
      }

      if (this.machineType === MachineType.Forwarder) {
        return this.compareLocalized(this.getMachineSizeLabel(a.size), this.getMachineSizeLabel(b.size));
      }

      return 0;
    });
  }

  private compareLocalized(a: string, b: string): number {
    const safeA = a ?? '';
    const safeB = b ?? '';
    return safeA.localeCompare(safeB, 'sv', { sensitivity: 'base' });
  }

  private isPropertyFiltered(filterItems: FilterItem[], property: string): boolean {
    return filterItems.every(f => !f.activated) || filterItems.some(f => f.value === property && f.activated);
  }

  private getCurrentMaxId(): number {
    return this.machinePerformances.reduce((max, item) => item.id > max ? item.id : max, 0);
  }

  private createNewItem(): DefaultMachinePerformanceListDto {
    return {
      id: 0,
      type: MachinePerformanceType.Regeneration,
      machineType: this.machineType,
      area: ProductionTechnicianArea.Gotaland,
      size: MachineSize.Small,
      g5InM3FubPerHour: 0,
      meanStemVolumeInM3FubPerTree: 0,
      transportDistanceForwarding: 0
    };
  }

  isValidNewItem(): boolean {
    return this.newItem.type !== null && this.newItem.machineType !== null;
  }

  private createSkeletonRows(): DefaultMachinePerformanceListDto[] {
    return Array.from({ length: this.skeletonRowCount }).map(() => ({
      id: 0,
      type: MachinePerformanceType.Regeneration,
      machineType: this.machineType,
      area: ProductionTechnicianArea.Gotaland,
      size: MachineSize.Small,
      g5InM3FubPerHour: 0,
      meanStemVolumeInM3FubPerTree: 0,
      transportDistanceForwarding: 0
    }));
  }

  private scheduleHighlightClear(): void {
    this.clearHighlightTimer();
    this.highlightClearTimeoutId = setTimeout(() => {
      this.highlightedRowIds.clear();
    }, 4000);
  }

  private clearHighlightTimer(): void {
    if (this.highlightClearTimeoutId !== null) {
      clearTimeout(this.highlightClearTimeoutId);
      this.highlightClearTimeoutId = null;
    }
  }

  isRowHighlighted(element: DefaultMachinePerformanceListDto): boolean {
    return this.highlightedRowIds.has(element.id);
  }

  getMachinePerformanceTypeLabel(value: string | MachinePerformanceType): string {
    if (typeof value === 'number') {
      return this.machinePerformanceTypeLabels[value] ?? value.toString();
    }
    return value;
  }

  getAreaLabel(value: ProductionTechnicianArea | string): string {
    if (typeof value === 'number') {
      return this.productionTechnicianAreaLabels[value] ?? value.toString();
    }
    return value;
  }

  getMachineSizeLabel(value: MachineSize | string): string {
    if (typeof value === 'number') {
      return this.machineSizeLabels[value] ?? value.toString();
    }
    return value;
  }
}
